import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import type { CommentAutoReplyRule, CommentPlatform, SocialComment } from '@/types/outreach';

// ─── Auto-Reply Rules ──────────────────────────────────────────────────────────

interface RulesResponse {
  count: number;
  results: CommentAutoReplyRule[];
}

export const useCommentAutoReplyRules = (platform?: CommentPlatform | '') => {
  return useQuery({
    queryKey: ['comment-auto-reply-rules', platform],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (platform) params.platform = platform;
      const { data } = await api.get<RulesResponse>(API_ROUTES.commentAutoReplyRules, { params });
      return data;
    },
  });
};

export const useSaveCommentAutoReplyRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      rule: Partial<CommentAutoReplyRule> & { platform: CommentPlatform; name: string },
    ) => {
      if (rule.id) {
        const { data } = await api.put<CommentAutoReplyRule>(
          `${API_ROUTES.commentAutoReplyRules}${rule.id}/`,
          rule,
        );
        return data;
      }
      const { data } = await api.post<CommentAutoReplyRule>(API_ROUTES.commentAutoReplyRules, rule);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comment-auto-reply-rules'] });
    },
  });
};

export const useDeleteCommentAutoReplyRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${API_ROUTES.commentAutoReplyRules}${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comment-auto-reply-rules'] });
    },
  });
};

// ─── Facebook Comments ─────────────────────────────────────────────────────────

interface CommentsResponse {
  count: number;
  results: SocialComment[];
}

export const useFacebookComments = (search?: string, postId?: string) => {
  return useQuery({
    queryKey: ['facebook-comments', search, postId],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (postId) params.post_id = postId;
      const { data } = await api.get<CommentsResponse>(API_ROUTES.messengerComments, { params });
      return data;
    },
    refetchInterval: 10000,
  });
};

// ─── Instagram Comments ────────────────────────────────────────────────────────

export const useInstagramComments = (search?: string, mediaId?: string) => {
  return useQuery({
    queryKey: ['instagram-comments', search, mediaId],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (mediaId) params.media_id = mediaId;
      const { data } = await api.get<CommentsResponse>(API_ROUTES.instagramComments, { params });
      return data;
    },
    refetchInterval: 10000,
  });
};
